import { Box } from '@mui/material';
import React, { useEffect, useRef } from 'react';

const SCHEDULING_CSS = 'https://calendar.google.com/calendar/scheduling-button-script.css';
const SCHEDULING_JS = 'https://calendar.google.com/calendar/scheduling-button-script.js';
const DEFAULT_SCHEDULING_URL =
    'https://calendar.google.com/calendar/appointments/schedules/AcZssZ2idsE55GS_GeWBiAGYrfseTwWGJ7SWzEdm7hEIC79aUZOV3Ybv3NHB87K5XPvh09hIAqp0BBcw?gv=true';

interface GoogleCalendarWidgetProps {
    url?: string;
    color?: string;
    label?: string;
}

function loadStylesheet(href: string): void {
    if (document.querySelector(`link[href="${href}"]`)) {
        return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
}

function loadScript(src: string): Promise<void> {
    const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
    if (existing?.dataset.loaded === 'true') {
        return Promise.resolve();
    }
    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener('load', () => resolve(), { once: true });
            existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
        });
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.addEventListener(
            'load',
            () => {
                script.dataset.loaded = 'true';
                resolve();
            },
            { once: true },
        );
        script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
        document.body.appendChild(script);
    });
}

const GoogleCalendarWidget: React.FC<GoogleCalendarWidgetProps> = ({
    url = DEFAULT_SCHEDULING_URL,
    color = '#877010',
    label = 'Book an appointment',
}) => {
    const targetRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let cancelled = false;
        const target = targetRef.current;
        if (!target) {
            return undefined;
        }

        loadStylesheet(SCHEDULING_CSS);

        loadScript(SCHEDULING_JS)
            .then(() => {
                if (cancelled || !targetRef.current) {
                    return;
                }
                const calendarApi = (window as Window & {
                    calendar?: {
                        schedulingButton: {
                            load: (options: {
                                url: string;
                                color: string;
                                label: string;
                                target: HTMLElement;
                            }) => void
                        }
                    }
                }).calendar;
                calendarApi?.schedulingButton.load({
                    url,
                    color,
                    label,
                    target: targetRef.current,
                });
            })
            .catch(() => {
                // Widget is optional; intake form still works if Google's script fails.
            });

        return () => {
            cancelled = true;
            if (target) {
                target.innerHTML = '';
            }
        };
    }, [url, color, label]);

    return <Box ref={targetRef} data-calendar-widget sx={{ display: 'flex', justifyContent: 'center', py: 2 }} />;
};

export default GoogleCalendarWidget;
